<template>
  <div>
    <div v-for="plan in plans" :key="plan.code" class="mb-6">
      <h4 class="text-subtitle-1 font-weight-bold mb-2">{{ plan.name }} · {{ planSuffix }}</h4>
      <v-simple-table dense class="actuarial__table">
        <thead>
          <tr>
            <th>{{ unitLabel }}</th>
            <th class="text-right">{{ priceLabel }}</th>
            <th class="text-right" :title="petsHint">{{ petsLabel }}</th>
            <th class="text-right">{{ costLabel }}</th>
            <th class="text-right">Margen</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <template v-for="row in plan.rows">
            <tr :key="`${plan.code}-${row.from}`" class="actuarial__row" @click="toggle(plan.code, row.from)">
              <td>{{ range(row.from, row.to) }}</td>
              <td class="text-right">
                {{ money(row.price) }}
                <div v-if="raisedByFloor(row)" class="actuarial__floor" :title="`La lista (${money(row.listPrice)}) no cubre el piso de margen: el precio sube hasta el piso`">
                  lista {{ money(row.listPrice) }}
                </div>
              </td>
              <td class="text-right">{{ num(row.petsPerUnit, 2) }}</td>
              <td class="text-right">{{ money(row.costPerUnit) }}</td>
              <td class="text-right font-weight-bold">{{ pct(row.margin) }}</td>
              <td class="text-right">
                <v-icon small>{{ isOpen(plan.code, row.from) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </td>
            </tr>
            <tr v-if="isOpen(plan.code, row.from)" :key="`${plan.code}-${row.from}-detail`">
              <td colspan="6" class="actuarial__detail">
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Servicio</th>
                      <th class="text-right">{{ costLabel }}</th>
                      <th class="text-right">Peso en el costo</th>
                      <th>Origen</th>
                      <th>Confianza</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="service in sortedServices(row)" :key="service.serviceCode">
                      <td>{{ serviceName(service.serviceCode) }}</td>
                      <td class="text-right">{{ money(service.cost) }}</td>
                      <td class="text-right">{{ pct(service.share) }}</td>
                      <td>{{ sourceLabel(service.source) }}</td>
                      <td>{{ service.confidence || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
    </div>
  </div>
</template>

<script>
import { formatMoney } from '../utils/b2b-quotes.utils';
import { num, pct, rangeText, serviceName, SOURCE_LABELS } from '../utils/actuarial.utils';

/** Price, cost per unit (employee or insured) and margin of each standard plan, tier by tier, with its cost by service. */
export default {
  name: 'ActuarialPlanMargins',
  props: {
    plans: { type: Array, default: () => [] },
    currency: { type: String, default: 'CLP' },
    planSuffix: { type: String, default: 'colectivo' },
    unitLabel: { type: String, default: 'Colaboradores' },
    priceLabel: { type: String, default: 'Precio lista' },
    petsLabel: { type: String, default: 'Mascotas por colaborador' },
    petsHint: {
      type: String,
      default: 'Mascotas activas por colaborador: el costo de una membresía vendida (1 por colaborador) se reparte entre ellas'
    },
    costLabel: { type: String, default: 'Costo por membresía' }
  },
  data() {
    return { open: {} };
  },
  methods: {
    key(plan, from) {
      return `${plan}-${from}`;
    },
    isOpen(plan, from) {
      return Boolean(this.open[this.key(plan, from)]);
    },
    toggle(plan, from) {
      this.$set(this.open, this.key(plan, from), !this.isOpen(plan, from));
    },
    /** True when the list price falls under the cost and the plan is priced at the margin floor. */
    raisedByFloor(row) {
      return row.listPrice !== undefined && row.price > row.listPrice + 0.5;
    },
    sortedServices(row) {
      return [...row.services].sort((a, b) => b.cost - a.cost);
    },
    money(value) {
      return formatMoney(value, this.currency);
    },
    range: rangeText,
    pct,
    num,
    serviceName,
    sourceLabel(source) {
      return SOURCE_LABELS[source] ?? source;
    }
  }
};
</script>

<style scoped>
.actuarial__table {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
}
.actuarial__row {
  cursor: pointer;
}
.actuarial__floor {
  font-size: 11px;
  white-space: nowrap;
  text-decoration: line-through;
  color: var(--pw-warning);
}
.actuarial__detail {
  background: var(--pw-surface-subtle);
  padding: 8px 16px 16px;
}
</style>
